/*
 * @Author: Chao Yang
 * @Date: 2019-12-12 03:30:03
 * @LastEditor: Chao Yang
 * @LastEditTime: 2022-07-01 15:19:42
 */
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import * as Document from 'src/app/domain/model/document';
import { BaseProxy } from '../base.proxy';
import { ISummary } from '../../model/account/i-summary.model';
import { IAccountDetail } from '../../model/account';
import { DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { IDateRange } from '../../model/dashboard';
import * as IDtoAccountGroup from 'src/app/domain/model/account-group/index';
import { environment } from 'src/environments/environment';

@Injectable()
export class AccountProxy extends BaseProxy {
  /**
   *
   */
  constructor(_http: HttpClient) {
    super(_http);
  }

  getAccounts(
    fundIds: number[],
    accounts: IDtoAccountGroup.IAccountItem[]
  ): Observable<Document.IAccount[]> {
    return this._http.post<Document.IAccount[]>('/ip/api/account', {
      fundIds,
      accounts,
    });
  }

  GetReportAccountDetail(
    programId: number,
    instanceId: number,
    accountId: number,
    start: Date,
    end: Date,
    viewDataForAllInvestors: boolean
  ): Observable<IAccountDetail> {
    start = start ? start : new Date(2000, 0, 1);
    end = end ? end : new Date(2000, 0, 1);

    return this._http.get<IAccountDetail>(
      `/ip/api/account/program/${programId}/instance/${instanceId}/crmAccount/${accountId}/detail?start=${this.getDateStr(
        start
      )}&end=${this.getDateStr(
        end
      )}&viewDataForAllInvestors=${viewDataForAllInvestors}`
    );
  }

  GetReportAccountSummary(
    programId: number,
    accountId: number,
    viewDataForAllInvestors: boolean
  ): Observable<ISummary> {
    return this._http.get<ISummary>(
      `/ip/api/account/program/${programId}/crmAccount/${accountId}/summary?viewDataForAllInvestors=${viewDataForAllInvestors}`
    );
  }

  GetReportAccountSummary2023(
    programId: number,
    accountId: number,
    viewDataForAllInvestors: boolean,
    start: Date,
    end: Date
  ): Observable<ISummary> {
    start = start ? start : new Date(2000, 0, 1);
    end = end ? end : new Date(2000, 0, 1);
    return this._http.get<ISummary>(
      `/ip/api/account/program/${programId}/crmAccount/${accountId}/summary/2023?viewDataForAllInvestors=${viewDataForAllInvestors}&start=${this.getDateStr(
        start
      )}&end=${this.getDateStr(end)}`
    );
  }

  //date jump
  //NOTE:this feature has been removed.
  GetReportUserProgramDateRange(
    programId: number,
    instanceId: number,
    accountId: number,
    start: Date,
    end: Date
  ) {
    start = start ? start : new Date(2000, 0, 1);
    end = end ? end : null;
    return this._http.get<IDateRange>(
      `/ip/api/account/program/${programId}/instance/${instanceId}/crmAccount/${accountId}/dateRange?start=${this.getDateStr(
        start
      )}&end=${this.getDateStr(end)}`
    );
  }

  getAccountGroups(): Observable<IDtoAccountGroup.IItem[]> {
    if (environment.feature.newApi === false) {
      return this._http.get<IDtoAccountGroup.IItem[]>(`/ip/api/account/group`);
    } else {
      return this._http.get<IDtoAccountGroup.IItem[]>(
        `/ip-api/api/accountgroup`
      );
    }
  }

  GetReportUserProgramITDEnd(
    programId: number,
    instanceId: number,
    accountId: number
  ) {
    return this._http.get<string>(
      `/ip/api/account/program/${programId}/instance/${instanceId}/crmAccount/${accountId}/itd/end`
    );
  }

  getTransactions(conditions: any[]): Observable<
    {
      effectiveDate: number;
      fundName: string;
      accountName: string;
      transactionType: string;
      amount: number;
    }[]
  > {
    return this._http.post<any>(
      `/ip/api/drilldown/investor/transaction`,
      conditions
    );
  }

  getBalanceTrans(conditions: any[]): Observable<
    {
      effectiveDate: number;
      fundName: string;
      accountName: string;
      transactionType: string;
      amount: number;
    }[]
  > {
    return this._http.post<any>(
      `/ip/api/drilldown/investor/transaction/balance`,
      conditions
    );
  }

  getPNLTransactions(conditions: any[]): Observable<
    {
      effectiveDate: number;
      fundName: string;
      accountName: string;
      transactionType: string;
      amount: number;
    }[]
  > {
    return this._http.post<any>(
      `/ip/api/drilldown/investor/transaction/netpnl`,
      conditions
    );
  }

  getOutstandingCommitment(conditions: any[]): Observable<
    {
      effectiveDate: number;
      fundName: string;
      accountName: string;
      transactionType: string;
      amount: number;
      percentage: number;
    }[]
  > {
    return this._http.post<any>(
      `/ip/api/drilldown/investor/transaction/outstanding/commitment`,
      conditions
    );
  }

  getTransactions2023(
    conditions: any[],
    displayClosedAccounts: boolean
  ): Observable<
    {
      effectiveDate: number;
      fundName: string;
      accountName: string;
      transactionType: string;
      amount: number;
    }[]
  > {
    return this._http.post<any>(
      `/ip/api/drilldown/investor/transaction/2023?displayClosedAccounts=${displayClosedAccounts}`,
      conditions
    );
  }

  getBalanceTrans2023(
    conditions: any[],
    displayClosedAccounts: boolean
  ): Observable<
    {
      effectiveDate: number;
      fundName: string;
      accountName: string;
      transactionType: string;
      amount: number;
    }[]
  > {
    return this._http.post<any>(
      `/ip/api/drilldown/investor/transaction/balance/2023?displayClosedAccounts=${displayClosedAccounts}`,
      conditions
    );
  }

  getPNLTransactions2023(
    conditions: any[],
    displayClosedAccounts: boolean
  ): Observable<
    {
      effectiveDate: number;
      fundName: string;
      accountName: string;
      transactionType: string;
      amount: number;
    }[]
  > {
    return this._http.post<any>(
      `/ip/api/drilldown/investor/transaction/netpnl/2023?displayClosedAccounts=${displayClosedAccounts}`,
      conditions
    );
  }

  getOutstandingCommitment2023(
    conditions: any[],
    displayClosedAccounts: boolean
  ): Observable<
    {
      effectiveDate: number;
      fundName: string;
      accountName: string;
      transactionType: string;
      amount: number;
      percentage: number;
    }[]
  > {
    return this._http.post<any>(
      `/ip/api/drilldown/investor/transaction/outstanding/commitment/2023?displayClosedAccounts=${displayClosedAccounts}`,
      conditions
    );
  }

  getReportUserProgramDateRange(
    programId: number,
    instanceId: number,
    accountId: number
  ) {
    return this._http.get<IDateRange>(
      `/ip/api/account/program/${programId}/instance/${instanceId}/crmAccount/${accountId}/date-range`
    );
  }
}
